import React, { useEffect } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FeedbackApi } from '../api';
import { FeedbackSectionId, getInvestmentType } from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { AnswersBySectionIdResponse } from './api';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import {
  DIRECT_COST_QUESTION_ID,
  FINAL_BUDGET_QUESTION_ID,
  getBudgetDollarAmount,
  getFinalizedBudget,
  getTotalBudget,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
} from 'npo/pages/ReportBuilder/Components/Budget/utils';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { useAnswersBySectionContext } from 'shared/providers/AnswersBySectionProvider';

const getTotalFinalBudget = (answer?: string): number | undefined => {
  if (!answer) return undefined;
  const final = getFinalizedBudget(answer);
  if (!final) return undefined;
  return (
    (Number(final.directCost?.replace(/,/g, '')) || 0) +
    (Number(final.overheadCost?.replace(/,/g, '')) || 0) +
    (Number(final.inKindDonations?.replace(/,/g, '')) || 0)
  );
};

const BudgetText: React.FC<{
  num?: number | string | null;
  isAmount?: boolean;
  text: string;
}> = ({ text, num, isAmount }) => {
  if (num === undefined) return null;
  return (
    <TrueImpactText variant="body-small">
      <strong>{text}:</strong>{' '}
      {isAmount ? `$${numberWithCommas(num || 0)}` : num || 'Unknown'}
    </TrueImpactText>
  );
};

interface FeedbackBudgetProps {
  reportId: number;
  report: FeedbackApi;
  sectionId: FeedbackSectionId;
  allocationAnswersRequestState: RequestState<AnswersBySectionIdResponse>;
}

const StyledDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACERS.medium};
  justify-content: space-between;
  background-color: ${COLORS.grayLightest};
  padding: ${SPACERS.medium} ${SPACERS.extraLarge};
`;

const FeedbackBudget: React.FC<FeedbackBudgetProps> = ({
  report,
  sectionId,
  allocationAnswersRequestState,
}) => {
  const {
    answers: a,
    handleSection,
    requestAnswerState,
  } = useAnswersBySectionContext();

  useEffect(() => {
    handleSection(report.dbId, 'budget');
    handleSection(report.dbId, 'finalize_report');
  }, []);

  if (sectionId !== 'allocation' && sectionId !== 'budget') return null;

  const reportId = report.dbId;
  const initialDirectCost = getBudgetDollarAmount(
    a[reportId]?.['budget'][DIRECT_COST_QUESTION_ID]?.text,
  );
  const initialOverheadCost = getBudgetDollarAmount(
    a[reportId]?.['budget'][OVERHEAD_COST_QUESTION_ID]?.text,
  );
  const initialInKindDonations = getBudgetDollarAmount(
    a[reportId]?.['budget'][IN_KIND_DONATIONS_QUESTION_ID]?.text,
  );

  const totalCost = getTotalBudget(
    initialDirectCost,
    initialOverheadCost,
    initialInKindDonations,
  );

  const investmentType = getInvestmentType(
    allocationAnswersRequestState.data?.allNpoReportAnswers,
  );

  if (requestAnswerState.type === 'REQUEST_START') {
    return <TrueImpactLoading singleLine={true} />;
  }

  const totalFinalBudget =
    getTotalFinalBudget(
      a[reportId]?.['finalize_report']?.[FINAL_BUDGET_QUESTION_ID]?.text,
    ) || 0;
  const totalBudget = totalCost || totalFinalBudget || report.totalCost;
  const totalDonations = report.totalDonations || report.claim || report.budget;
  const claimPercentage = (totalDonations / totalBudget) * 100;

  return (
    <StyledDiv>
      <BudgetText
        text="Investment type"
        num={investmentType || report.investmentType}
      />
      <BudgetText isAmount={true} text="Total donations" num={totalDonations} />
      <BudgetText isAmount={true} text="Total cost" num={totalBudget} />
      <BudgetText
        text="Claim"
        num={
          investmentType === 'Foundational'
            ? '100%'
            : Number.isFinite(claimPercentage)
            ? `${claimPercentage.toFixed(2)}%`
            : 'Unknown'
        }
      />
    </StyledDiv>
  );
};

export default FeedbackBudget;
